import { formatReentryPlanAddress, stripHtmlTags } from './honest-jobs-format.util';

/** @typedef {import('./honest-jobs-reentry-plan.schema').HonestJobsReentryPlanResource} HonestJobsReentryPlanResource */

const getStepAction = (step) => step?.action ?? '';
const getStepContact = (step) => step?.contact ?? '';

const combineNextStepParts = (action, contact) => {
  if (!action) {
    return contact;
  }
  if (!contact) {
    return action;
  }
  return `${action}: ${contact}`;
};

const formatSingleNextStep = (step) =>
  combineNextStepParts(getStepAction(step), getStepContact(step));

const formatNextSteps = (steps = []) =>
  steps.map(formatSingleNextStep).filter(Boolean).join('\n');

const getProgramRules = (programDetails) =>
  Array.isArray(programDetails.rules) ? programDetails.rules : [];

const getSupportedLanguages = (programDetails) =>
  Array.isArray(programDetails.supported_languages) ? programDetails.supported_languages : [];

const getProgramDetailField = (programDetails, fieldName) => programDetails[fieldName] ?? '';

const buildResourceDetailFields = (programDetails, strippedDescription) => {
  const rules = getProgramRules(programDetails);
  const supportedLanguages = getSupportedLanguages(programDetails);

  return {
    Eligibility: rules.join('\n'),
    Availability: getProgramDetailField(programDetails, 'availability'),
    Description: strippedDescription,
    Languages: supportedLanguages.join(', '),
    Cost: getProgramDetailField(programDetails, 'free_or_reduced'),
    'Next Steps': formatNextSteps(programDetails.next_steps),
    Website: getProgramDetailField(programDetails, 'website_url'),
    Facebook: getProgramDetailField(programDetails, 'facebook_url'),
    'Coverage Area': getProgramDetailField(programDetails, 'coverage_description'),
    Required: rules[rules.length - 1] ?? '',
    'Last Reviewed': getProgramDetailField(programDetails, 'validation_date'),
  };
};

/**
 * Maps a reentry-plan `resources[]` item to the Honest Jobs widget `IResource` shape.
 *
 * @param {HonestJobsReentryPlanResource} resource
 */
export const mapReentryPlanResourceToHonestJobsResource = (resource) => {
  const programDetails = resource?.programDetails ?? {};
  const strippedDescription = stripHtmlTags(programDetails.description);

  return {
    name: resource?.programName ?? programDetails.name ?? '',
    providerName: resource?.providerName ?? programDetails.provider_name ?? '',
    description: strippedDescription,
    address: formatReentryPlanAddress(resource?.address),
    details: buildResourceDetailFields(programDetails, strippedDescription),
  };
};
